import {
  Show,
  SimpleShowLayout,
  TextField,
  Title,
  useShowContext,
  useRecordContext,
} from "react-admin";
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Stack,
} from "@mui/material";
import type { RaRecord } from "react-admin";
import {
  googleCalendarColorHex,
  googleCalendarColorLabel,
} from "@/utils/google-calendar-event-colors";

const HallChargesTable = () => {
  const { record } = useShowContext();
  const hallCharges = (record?.hallCharges as RaRecord[] | undefined) || [];

  if (hallCharges.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
        No hall charges configured for this purpose.
      </Typography>
    );
  }

  return (
    <TableContainer component={Paper} sx={{ mt: 2 }}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Hall</TableCell>
            <TableCell align="right">Rent</TableCell>
            <TableCell align="right">Deposit</TableCell>
            <TableCell align="right">AC Charges</TableCell>
            <TableCell align="right">Kitchen Cleaning</TableCell>
            <TableCell align="center">Include Thaal Charges</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {hallCharges.map((charge, index) => (
            <TableRow key={(charge.hallId as string) || String(index)}>
              <TableCell>
                {(charge.hallName as string) ||
                  (charge.hallShortCode as string) ||
                  String(charge.hallId)}
              </TableCell>
              <TableCell align="right">
                {charge.rent ? `₹${Number(charge.rent).toLocaleString()}` : "-"}
              </TableCell>
              <TableCell align="right">
                {charge.deposit ? `₹${Number(charge.deposit).toLocaleString()}` : "-"}
              </TableCell>
              <TableCell align="right">
                {charge.acCharges ? `₹${Number(charge.acCharges).toLocaleString()}` : "-"}
              </TableCell>
              <TableCell align="right">
                {charge.kitchenCleaning
                  ? `₹${Number(charge.kitchenCleaning).toLocaleString()}`
                  : "-"}
              </TableCell>
              <TableCell align="center">
                <Chip
                  label={charge.includeThaalCharges ? "Yes" : "No"}
                  color={charge.includeThaalCharges ? "success" : "default"}
                  size="small"
                />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

function CalendarColorField() {
  const record = useRecordContext();
  const colorId = typeof record?.calendarColorId === "string" ? record.calendarColorId.trim() : "";
  const hex = googleCalendarColorHex(colorId);
  const label = googleCalendarColorLabel(colorId);

  return (
    <Stack direction="row" alignItems="center" gap={1} useFlexGap>
      {hex ? (
        <Box
          aria-hidden
          sx={{
            width: 18,
            height: 18,
            borderRadius: 0.5,
            bgcolor: hex,
            border: "1px solid",
            borderColor: "divider",
            flexShrink: 0,
          }}
        />
      ) : null}
      <Typography variant="body2">
        {label ? `${label} (${colorId})` : "Default (Peacock)"}
      </Typography>
    </Stack>
  );
}

export const BookingPurposeShow = () => (
  <Show>
    <SimpleShowLayout>
      <Title title="Purpose Details" />
      <TextField source="name" label="Name" />
      <TextField source="id" label="Purpose ID" />
      <Box sx={{ mt: 1, mb: 1 }}>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          Google Calendar color
        </Typography>
        <CalendarColorField />
      </Box>
      <Box sx={{ mt: 3 }}>
        <Typography variant="h6" gutterBottom>
          Hall Charges
        </Typography>
        <HallChargesTable />
      </Box>
    </SimpleShowLayout>
  </Show>
);

export default BookingPurposeShow;
